import React, { useState, useEffect } from "react";
import {
    Box,
    Text,
    VStack,
    HStack,
    Button,
    useClipboard,
    Divider,
    Image,
    Flex,
    useToast
} from "@chakra-ui/react";
import QRCode from "react-qr-code";
import { useLocation, useNavigate } from "react-router-dom";
import { _CRYPTOCURRENCIES, ApiKey } from "../../common/constant";
import CooldownButton from "../../components/cooldown-button";
import { useDispatch } from "react-redux";
import { callback, setSessionToken } from "../../redux/transaction/action";
import ApiEngine from "../../common/api-engine";
import { stringIsNullOrEmpty } from "common/util";
import Routes from '../../common/api_routes';
import moment from "moment-timezone";

/// <summary>
/// Author: CK
/// </summary>
const CryptoTransactionDetail = () => {
    const _dispatch = useDispatch();
    const navigate = useNavigate();
    const [transaction, setTransaction] = useState(null);
    const _location = useLocation();
    const _queryParams = new URLSearchParams(_location.search);
    const _sessionToken = _queryParams.get("sk")?.replace(/ /g, '+');
    const _sign = _queryParams.get("s")?.replace(/ /g, '+');;
    const toast = useToast();

    const { onCopy: onCopyWallet, hasCopied: walletCopied } = useClipboard(
        transaction?.receiverWalletAddress
    );
    const { onCopy: onCopyAmount, hasCopied: amountCopied } = useClipboard(
        transaction?.cryptoAmount.toString()
    );

    const selectedCrypto = _CRYPTOCURRENCIES.find(i => i.id === transaction?.cryptocurrencyId)?.name;

    const [timeLeft, setTimeLeft] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [firstInit, setFirstInit] = useState(true);

    /// <summary>
    /// Author: CK
    /// </summary>
    useEffect(() => {
        if (stringIsNullOrEmpty(_sessionToken) || stringIsNullOrEmpty(_sign)) {
            _dispatch(callback("Unauthorized access."));
            return;
        }

        _dispatch(setSessionToken(_sessionToken));
        getTransactionDetail();
    }, []);

    /// <summary>
    /// Author: Saitama
    /// </summary>
    useEffect(() => {
        let verifyInterval;

        if (transaction && firstInit) {
            verifyInterval = setInterval(() => {
                validateTransaction(true);
            }, 15000);

            setFirstInit(false);
        }

        return () => {
            if (verifyInterval) {
                clearInterval(verifyInterval);
            }
        };
    }, [transaction]);

    /// <summary>
    /// Author: CK
    /// </summary>
    // Countdown logic
    useEffect(() => {
        if (transaction && timeLeft != null) {
            if (timeLeft <= 0) {
                _dispatch(callback("Transaction expired."));

                return;
            }

            const interval = setInterval(() => {
                setTimeLeft((prev) => Math.max(0, prev - 1));
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [timeLeft, navigate, transaction?.id]);

    /// <summary>
    /// Author: CK
    /// </summary>
    // Format time in mm:ss
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    };

    /// <summary>
    /// Author: CK
    /// </summary>
    const getTransactionDetail = async () => {
        try {
            console.log("Getting transaction detail");

            const result = await ApiEngine.post(Routes._GET_TRANSACTION, {
                "signature": _sign,
                "sessionToken": _sessionToken
            });

            if (result.success) {
                let transactionData = result[ApiKey._API_DATA_KEY]["transaction"];

                if (transactionData != undefined) {
                    // const now = new Date();
                    const now = moment.tz(new Date(), "Asia/Singapore");
                    const expiryTime = new Date(transactionData?.expiredAt);

                    setTimeLeft(Math.max(0, Math.floor((expiryTime - now) / 1000)));
                    setTransaction(transactionData);
                }
                else {
                    _dispatch(callback("Transaction not founnd. Please try again."));
                }
            }
            else {
                _dispatch(callback(result.message || "Something went wrong."));
            }
        } catch (error) {
            _dispatch(callback(error || "Something went wrong."));
        }
    }

    /// <summary>
    /// Author: CK
    /// </summary>
    const validateTransaction = async (skipLoading = false) => {
        try {
            setValidationError(null);

            const result = await ApiEngine.post(Routes._VALIDATE_TRANSACTION, {
                "signature": _sign,
                "transactionId": transaction?.id
            }, { headers: { "skipLoading": true } });

            console.log("validate", result);

            if (result.success) {
                _dispatch(callback(result.message, transaction?.id));
            }
            else {
                if (result.callback) {
                    _dispatch(callback(result.message || "Something went wrong.", transaction?.id));
                    return;
                }

                setValidationError(result.message || "Something went wrong. Please try again.");

                toast({
                    title: "Error",
                    description: result.message || "Something went wrong. Please try again.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to initiate the transaction.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <Box maxW="800px" mx="auto" p={"35px"}>
            <div className="header-logo">
                <Image src="/logo.png" />
            </div>
            <Text fontSize="2xl" fontWeight="bold" mb={"10px"}>
                {selectedCrypto} Transaction Details
            </Text>
            {
                transaction != undefined ?
                    <>
                        <VStack spacing={"10px"}>
                            {/* Countdown Timer */}
                            <Box textAlign="center" w="100%" p={"5px"} bg="yellow.100" borderRadius="md">
                                <Text fontSize="md" fontWeight="bold">
                                    Time Left: {formatTime(timeLeft)}
                                </Text>
                                <Text fontSize="sm" color="red.500">
                                    Please complete the transfer before the time expires.
                                </Text>
                            </Box>
                            <HStack justify="space-between" w="100%">
                                <Text>Fiat Amount:</Text>
                                <Text fontWeight="bold">{transaction?.fiatAmount} MYR</Text>
                            </HStack>
                            <HStack justify="space-between" w="100%">
                                <Text>Crypto Amount:</Text>
                                <Flex flexDirection="column" alignItems="flex-end" justifyContent={'flex-end'}>
                                    <Text color="red.500" fontWeight="bold" fontSize={"20px"}>{transaction?.cryptoAmount} {selectedCrypto}</Text>
                                    {/* Copy Crypto Amount Button */}
                                    <Button
                                        colorScheme="gray"
                                        onClick={onCopyAmount}
                                        variant="outline"
                                        size={"sm"}
                                    >
                                        {amountCopied ? "Copied Amount" : "Copy Amount"}
                                    </Button>
                                </Flex>
                            </HStack>

                            <Text fontWeight={"bold"} color="red.500" fontSize="sm" textAlign="center">
                                Transfer {transaction?.cryptoAmount} {selectedCrypto} (Transaction fee excluded) <br />
                                To avoid any delays, please add transaction fee on top of your crypto deposit amount to match exactly.
                            </Text>

                            <Divider />

                            {/* QR Code with Cryptocurrency Image */}
                            <Box textAlign="center" w="100%">
                                <Text fontSize="md" fontWeight="bold" marginBottom={"5px"}>
                                    Wallet Address:
                                </Text>
                                <Box position="relative" display="inline-block" mx="auto">
                                    {/* QR Code */}
                                    <QRCode
                                        value={transaction?.receiverWalletAddress}
                                        size={200}
                                        bgColor="#ffffff"
                                        fgColor="#000000"
                                        level="Q"
                                    />
                                    {/* Cryptocurrency Image in the Middle */}
                                    <Image
                                        src={_CRYPTOCURRENCIES.find(i => i.id === transaction?.cryptocurrencyId)?.image}
                                        alt={transaction?.cryptocurrency}
                                        boxSize="50px"
                                        borderRadius="full"
                                        position="absolute"
                                        top="50%"
                                        left="50%"
                                        transform="translate(-50%, -50%)"
                                        border="2px solid white"
                                    />
                                </Box>
                                <Text fontSize="sm" color="gray.600" mb={2}>
                                    <strong fontSize="md">{selectedCrypto}</strong> <br />
                                    {transaction?.receiverWalletAddress}
                                </Text>
                                {/* Copy Wallet Address Button */}
                                <Button
                                    colorScheme="blue"
                                    w="100%"
                                    onClick={onCopyWallet}
                                    variant="outline"
                                >
                                    {walletCopied ? "Copied Wallet Address" : "Copy Wallet Address"}
                                </Button>
                            </Box>
                            <CooldownButton
                                onClick={() => {
                                    validateTransaction();
                                }}
                                colorScheme="green"
                                buttonText="Check Transaction Status"
                            />
                            {
                                !stringIsNullOrEmpty(validationError) &&
                                <Text fontWeight={"bold"} color="red.500" fontSize="sm" textAlign="center">
                                    {validationError}
                                </Text>
                            }
                            <Divider />
                            {/* Notice Section */}
                            <Box p={4} bg="gray.100" borderRadius="md" border={"1px solid"} borderColor="gray.200">
                                <Text fontSize="lg" fontWeight="bold" mb={2}>
                                    Notice
                                </Text>
                                <VStack align="start" spacing={2}>
                                    <Text fontSize="sm" color="gray.700">
                                        1. Please ensure you do not transfer any cryptocurrency other than **{selectedCrypto}**, as the payment will fail if incorrect funds are sent.
                                    </Text>
                                    <Text fontSize="sm" color="gray.700">
                                        2. Transfer the exact amount of **{transaction?.cryptoAmount} {selectedCrypto} (Transaction fee excluded)** to avoid payment failure.
                                    </Text>
                                    <Text fontSize="sm" color="gray.700">
                                        3. After the transfer, please wait patiently for the blockchain confirmation process to complete.
                                    </Text>
                                </VStack>
                            </Box>
                        </VStack >
                    </>
                    :
                    <></>
            }
        </Box >
    );
};

export default CryptoTransactionDetail;
